import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
 import { Loader2, TrendingUp, Users, CreditCard, Percent, RefreshCw, Ticket, Target, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { usePartnerCodesStatsCache, TIMEFRAMES } from "@/hooks/usePartnerCodesStatsCache";
import { useAdminStatsGeneration } from "@/hooks/useAdminStatsGeneration";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const CHART_COLORS = [
  "hsl(var(--secondary))",
  "hsl(var(--primary))",
  "hsl(var(--info))",
  "hsl(var(--warning))",
  "hsl(var(--destructive))",
];

export const PartnerCodesStatsCard = () => {
  const {
    stats,
    chartData,
    generatedAt,
    isLoading,
    isRefreshing,
    timeframe,
    setTimeframe,
  } = usePartnerCodesStatsCache();
  
  const { generateStats, isGenerating } = useAdminStatsGeneration();
  
  const isManualGenerating = isGenerating("partner-codes" as any);
  const handleRefresh = () => generateStats("partner-codes" as any);

  const hasNoCache = !isLoading && !stats;

  // Auto-generate stats on first load if no cache exists
  useEffect(() => {
    if (hasNoCache && !isManualGenerating) {
      handleRefresh();
    }
  }, [hasNoCache]);

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-secondary" />
              Partner Codes Stats
            </CardTitle>
            <CardDescription className="text-xs">
              {TIMEFRAMES.find(t => t.value === timeframe)?.label || timeframe}
              {generatedAt && (
                <span className="ml-2 text-muted-foreground">
                  · Updated {formatDistanceToNow(new Date(generatedAt))} ago
                  {isRefreshing && <span className="ml-1 text-xs opacity-70">(refreshing...)</span>}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={handleRefresh}
              disabled={isManualGenerating || isRefreshing}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", (isManualGenerating || isRefreshing) && "animate-spin")} />
            </Button>
            <div className="flex gap-0.5">
              {TIMEFRAMES.map(({ value, label }) => (
                <Button
                  key={value}
                  variant={timeframe === value ? "secondary" : "ghost"}
                  size="sm"
                  className={cn("h-6 px-2 text-xs", timeframe === value && "font-bold")}
                  onClick={() => setTimeframe(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="py-3 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : hasNoCache ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-muted-foreground mb-4">No cached stats available</p>
            <Button onClick={handleRefresh} disabled={isManualGenerating}>
              {isManualGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Stats"
              )}
            </Button>
          </div>
        ) : (
          <>
            {/* Stats Grid - Inline compact */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <Ticket className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Total:</span>
                <span className="font-semibold">{stats?.totalCodes ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Active:</span>
                <span className="font-semibold text-success">{stats?.activeCodes ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Ticket className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Inactive:</span>
                <span className="font-semibold text-muted-foreground">{stats?.inactiveCodes ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Credits Type:</span>
                <span className="font-semibold">{stats?.creditsCodes ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Percent className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Percentage Type:</span>
                <span className="font-semibold">{stats?.percentageCodes ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Redemptions:</span>
                <span className="font-semibold">{stats?.totalRedemptions ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Credits Granted:</span>
                <span className="font-semibold">{stats?.totalCreditsGranted ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Unique Users:</span>
                <span className="font-semibold">{stats?.uniqueUsersRedeemed ?? 0}</span>
              </div>
             <div className="flex items-center gap-1.5">
               <Target className="h-3.5 w-3.5 text-secondary" />
               <span className="text-muted-foreground">Conversion:</span>
               <span className="font-semibold text-success">{stats?.conversionRate ?? 0}%</span>
             </div>
             <div className="flex items-center gap-1.5">
               <Activity className="h-3.5 w-3.5 text-secondary" />
               <span className="text-muted-foreground">Avg Events/User:</span>
               <span className="font-semibold">{stats?.avgEventsPerUser ?? 0}</span>
             </div>
             <div className="flex items-center gap-1.5">
               <Ticket className="h-3.5 w-3.5 text-secondary" />
               <span className="text-muted-foreground">Total Events Booked:</span>
               <span className="font-semibold">{stats?.totalEventsBookedByRedeemedUsers ?? 0}</span>
             </div>
            </div>

            {/* Charts - Smaller height */}
           <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Redemptions Over Time */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Redemptions</h4>
                <div className="h-28">
                  {chartData.redemptionsOverTime && chartData.redemptionsOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.redemptionsOverTime} margin={{ left: 0, right: 4 }}>
                        <XAxis
                          dataKey="name"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 8 }}
                          interval="preserveStartEnd"
                          tickLine={false}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                        />
                        <YAxis
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 8 }}
                          tickLine={false}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          width={24}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '6px',
                            fontSize: '11px'
                          }}
                        />
                        <Bar dataKey="redemptions" fill="hsl(var(--secondary))" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

              {/* Codes by Type */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Codes by Type</h4>
                <div className="h-28">
                  {chartData.codesByType && chartData.codesByType.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.codesByType}
                          dataKey="value"
                          nameKey="name"
                          cx="30%"
                          cy="50%"
                          outerRadius={35}
                        >
                          {chartData.codesByType.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend
                          layout="vertical"
                          align="right"
                          verticalAlign="middle"
                          wrapperStyle={{ fontSize: '10px', paddingLeft: '8px' }}
                          formatter={(value, entry: any) => `${value}: ${entry.payload.value}`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '6px',
                            fontSize: '11px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

              {/* Top Partners */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Top Partners</h4>
                <div className="h-28">
                  {chartData.topPartners && chartData.topPartners.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.topPartners} layout="vertical" margin={{ left: 0, right: 4 }}>
                        <XAxis
                          type="number"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 8 }}
                          tickLine={false}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 8 }}
                          tickLine={false}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          width={50}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '6px',
                            fontSize: '11px'
                          }}
                        />
                        <Bar dataKey="redemptions" fill="hsl(var(--info))" radius={[0, 2, 2, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

             {/* Top Codes by Engagement */}
             <div>
               <h4 className="text-xs font-medium text-muted-foreground mb-1">Top Codes by Engagement</h4>
               <div className="h-28">
                 {chartData.topCodesByEngagement && chartData.topCodesByEngagement.length > 0 ? (
                   <ResponsiveContainer width="100%" height="100%">
                     <BarChart data={chartData.topCodesByEngagement} layout="vertical" margin={{ left: 0, right: 4 }}>
                       <XAxis
                         type="number"
                         tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 8 }}
                         tickLine={false}
                         axisLine={{ stroke: 'hsl(var(--border))' }}
                       />
                       <YAxis
                         type="category"
                         dataKey="name"
                         tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 8 }}
                         tickLine={false}
                         axisLine={{ stroke: 'hsl(var(--border))' }}
                         width={60}
                       />
                       <Tooltip
                         contentStyle={{
                           backgroundColor: 'hsl(var(--card))',
                           border: '1px solid hsl(var(--border))',
                           borderRadius: '6px',
                           fontSize: '11px'
                         }}
                         formatter={(value: number, name: string) => {
                           if (name === 'eventsBooked') return [value, 'Events Booked'];
                           return [value, name];
                         }}
                       />
                       <Bar dataKey="eventsBooked" fill="hsl(var(--success))" radius={[0, 2, 2, 0]} />
                     </BarChart>
                   </ResponsiveContainer>
                 ) : (
                   <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                 )}
               </div>
             </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
